import React, { useEffect, useState } from 'react'
import { getSubjects } from '../../../../../../services/subjects/subjects.service'
import { handlerErrorV2 } from '../../../../../../utils/errors'
import DataGrid from '../../../../../table/DataGrid'
import TextNote from '../../../../../text/TextNote'
import ButtonStartIcon from '../../../../../button/ButtonStartIcon'
import { Checkbox, FormControlLabel, Paper, Typography } from '@mui/material'
import InputField from '../../../../../FormControl/input/InputField'
import Loading from '../../../../../Loading/Loading'
import CardKeyValue from '../../../../../card/cardKeyValue'

const FormSubject = ({ form, onChange, isLoading, currentCurriculum }) => {
  const [isLoadingSubject, toggleLoadingSubject] = useState(false)
  const [error, setError] = useState(null)
  const [subjectData, setSubjectData] = useState([])
  const [subjectDataTemp, setSubjectDataTemp] = useState([])
  const [columnsTable, setColumns] = useState([])
  const [groupBy, setGroupBy] = useState(null)
  const [filter, setFilter] = useState({})

  useEffect(() => {
    const fetchData = async () => {
      if (!form?.curriculumId) return
      toggleLoadingSubject(true)
      setError(null)
      try {
        const response = await getSubjects(form?.curriculumId, filter)
        if (!response?.data && response?.error) {
          setError(response.error.message || 'An error ocurred')
        } else {
          setSubjectData(response.data)
          setSubjectDataTemp(response.data)
        }
      } catch (error) {
        const message = handlerErrorV2(error)
        setError(message)
      } finally {
        toggleLoadingSubject(false)
      }
    }
    fetchData()
  }, [form?.curriculumId])

  useEffect(() => {
    const columns = [{ label: 'Name', key: 'name', align: 'left', width: 200, show: true, hide: false }]

    if (currentCurriculum) {
      const elements = {}
      if (currentCurriculum.elementsStructure) {
        setGroupBy(currentCurriculum.elementsStructure?._id)
        // elements[currentCurriculum.elementsStructure?._id] = {
        //   label: currentCurriculum.elementsStructure?.name,
        //   key: currentCurriculum.elementsStructure?._id,
        //   show: true,
        //   caret: true,
        //   hide: true,
        //   position: 1,
        //   width: 100,
        //   className: 'text-nowrap'
        // };

        const mapElements = (child) => {
          elements[child._id] = {
            label: child.name,
            key: child._id,
            show: true,
            caret: true,
            hide: true,
            position: Object.keys(elements).length + 1,
            width: 100,
            className: 'text-nowrap'
          }
          child?.children?.forEach((element) => {
            mapElements(element)
          })
        }

        currentCurriculum.elementsStructure?.children.forEach((child) => {
          mapElements(child)
        })
      }

      Object.keys(elements).forEach((key) => {
        columns.push(elements[key])
      })
    }

    columns.sort((a, b) => a.position - b.position)
    setColumns(columns)
  }, [currentCurriculum])

  const onSelect = (id) => {
    let newIds = form.subjectId || []
    if (newIds.includes(id)) {
      newIds = newIds.filter((item) => item !== id)
    } else {
      newIds.push(id)
    }

    onChange('subjectId', [...newIds])
  }

  const onSelectedAll = (checked) => {
    if (checked) {
      const newIds = subjectData.map((item) => item._id)
      onChange('subjectId', [...newIds])
    } else {
      onChange('subjectId', [])
    }
  }

  const onSelectGroupBy = (checked, ids = []) => {
    const subjs = form.subjectId || []
    const newIds = subjs.filter((item) => !ids.includes(item))
    if (checked) {
      onChange('subjectId', [...newIds, ...ids])
    } else {
      onChange('subjectId', [...newIds])
    }
  }

  const onSearch = (value) => {
    if (value.length === 0) {
      setSubjectData(subjectDataTemp)
    } else {
      const filterData = subjectDataTemp.filter((item) =>
        item.name.toLowerCase().includes(value.toLowerCase())
      )
      setSubjectData(filterData)
    }
  }

  return (
    <>
      <div className='flex gap-2 items-center justify-between items-center py-2'>
        <div className='flex gap-2'>
          <CardKeyValue
            className='min-w-100px max-w-150px'
            title='Code'
            value={form?.assessmentData?.code || ''}
          />
          <CardKeyValue
            className='min-w-150px max-w-200px'
            title='Name'
            value={form?.assessmentData?.name || ''}
          />
        </div>
        <div className='flex gap-2 items-center justify-between items-center py-2 w-full'>
          <div className='flex gap-2 justify-start'>
            <ButtonStartIcon
              variant='text'
              startIcon='bars-filter'
              title='Filter'
              color='primary'
              classNameIcon='text-3xl'
              size='large'
            >
              Filter
            </ButtonStartIcon>
            <div className='max-w-250px'>
              <InputField placeholder='Search' onChange={(e) => onSearch(e.target.value)} />
            </div>
          </div>
          <Typography>{form?.subject?.length} Subjects selected</Typography>
        </div>
      </div>
      <div className='absolute top-20 bottom-0 inset-x-0 MuiBox-root css-0'>
        {isLoading ? (
          <Loading />
        ) : (
          <DataGrid
            id='subjects'
            showSetting={false}
            groupBy={groupBy}
            rowsPerPage={subjectData?.length || 10}
            nameModule='subject-assessment-area'
            groupByRender={(value, { rows }) => {
              const ids = rows.map((row) => row._id) || []
              const filtered = form.subjectId?.filter((item) => ids.includes(item)) || []
              return (
                <div className='px-6'>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color='primary'
                        indeterminate={filtered.length > 0 && filtered.length < ids.length}
                        checked={filtered.length === ids.length}
                        onChange={(e) => onSelectGroupBy(e.target.checked, ids)}
                      />
                    }
                    label={
                      value === 'undefined'
                        ? `No ${currentCurriculum?.elementsStructure?.name || 'Learning Area'}`
                        : value
                    }
                  />
                </div>
              )
            }}
            rows={subjectData.map((row) => {
              const elements = {}
              Object.keys(row.curriculumElements || {}).forEach((element) => {
                const mapElements = (key, value, children) => {
                  const find = children?.find((child) => child._id === value.value)
                  if (find) {
                    elements[key] = find.name?.length > 30 ? find.name.substring(0, 30) + '...' : find.name
                    Object.keys(value?.children || {}).forEach((child) => {
                      const el = find.elements?.find((c) => c.elementStructureId === child)
                      if (el) {
                        mapElements(child, value.children[child], el.children)
                      }
                    })
                  }
                }
                mapElements(
                  element,
                  row.curriculumElements[element],
                  currentCurriculum.elements?.children || []
                )
              })
              const ids = form?.subjectId || []
              return {
                ...row,
                description: <TextNote notes={row.description || ''} />,
                ...elements,
                checkId: (
                  <Checkbox
                    id={`checkbox-id-subject-assessment-area-${row._id}`}
                    checked={ids?.includes(row._id)}
                    Add
                    commentMore
                    actions
                    onChange={() => onSelect(row._id)}
                    color='primary'
                  />
                )
              }
            })}
            loading={isLoading || isLoadingSubject}
            columns={[
              {
                show: true,
                key: 'checkId',
                align: 'center',
                hide: false,
                width: 15,
                label: (
                  <Checkbox
                    id={`checkbox-id-subject-assessment-area-header`}
                    checked={
                      form?.subject?.length > 0 ? form?.subject?.length === subjectData?.length : false
                    }
                    indeterminate={form?.subject?.length > 0 && form?.subject?.length < subjectData?.length}
                    onChange={(e) => onSelectedAll(e.target.checked)}
                    color='primary'
                  />
                )
              },
              ...columnsTable
            ]}
            labelNoRows="You don't have any subjects yet"
          />
        )}
      </div>
    </>
  )
}

export default FormSubject
